import { Controller } from "@hotwired/stimulus"

// Marketing header (layouts/silicon/_header). Desktop: hover-intent dropdowns,
// one open at a time, click/keyboard toggles. Below the lg breakpoint the same
// markup becomes a full-height sheet whose items act as accordions.
const DESKTOP = "(min-width: 992px)"
// Hover intent only where a real pointer hovers; a tablet at desktop width
// fires a synthetic mouseenter before its click and the two would fight.
const HOVERS = "(hover: hover)"
const FOCUSABLE = "a[href], button:not([disabled])"
const OPEN_DELAY = 60
const CLOSE_DELAY = 140

export default class extends Controller {
  static targets = ["item", "trigger", "burger"]

  connect() {
    this.mediaQuery = window.matchMedia(DESKTOP)
    this.hoverQuery = window.matchMedia(HOVERS)
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.closeAll()
    }
    this.onKeydown = (event) => {
      if (event.key === "Escape") this.escape()
      if (event.key === "Tab") this.trapTab(event)
    }
    this.onReset = () => this.reset()

    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("turbolinks:before-cache", this.onReset)
    this.mediaQuery.addEventListener("change", this.onReset)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("turbolinks:before-cache", this.onReset)
    this.mediaQuery.removeEventListener("change", this.onReset)
    this.clearTimers()
    this.setSheet(false)
  }

  // --- dropdown items ------------------------------------------------------

  toggle(event) {
    const item = event.currentTarget.closest("[data-site-nav-target~='item']")
    const opening = !item.classList.contains("is-open")
    this.clearTimers()
    if (this.desktop) this.closeAll()
    this.setOpen(item, opening)
  }

  enter(event) {
    if (!this.hovers) return
    const item = event.currentTarget
    clearTimeout(this.closeTimer)
    this.openTimer = setTimeout(() => {
      this.closeAll()
      this.setOpen(item, true)
    }, OPEN_DELAY)
  }

  leave(event) {
    if (!this.hovers) return
    const item = event.currentTarget
    clearTimeout(this.openTimer)
    this.closeTimer = setTimeout(() => this.setOpen(item, false), CLOSE_DELAY)
  }

  // --- mobile sheet --------------------------------------------------------

  toggleSheet() {
    this.setSheet(!this.sheetOpen)
  }

  // --- internals -----------------------------------------------------------

  get desktop() {
    return this.mediaQuery.matches
  }

  get hovers() {
    return this.desktop && this.hoverQuery.matches
  }

  get sheetOpen() {
    return this.element.classList.contains("is-sheet-open")
  }

  setOpen(item, open) {
    item.classList.toggle("is-open", open)
    const trigger = item.querySelector("[data-site-nav-target~='trigger']")
    if (trigger) trigger.setAttribute("aria-expanded", String(open))
  }

  closeAll() {
    this.itemTargets.forEach((item) => this.setOpen(item, false))
  }

  setSheet(open) {
    this.element.classList.toggle("is-sheet-open", open)
    document.documentElement.classList.toggle("sitenav-locked", open)
    if (this.hasBurgerTarget) {
      this.burgerTarget.setAttribute("aria-expanded", String(open))
      this.burgerTarget.setAttribute("aria-label", open ? "Close menu" : "Open menu")
    }
    if (open) {
      const first = this.element.querySelector("#sitenav-menu " + FOCUSABLE)
      if (first && this.hasBurgerTarget && document.activeElement === this.burgerTarget) first.focus()
    } else {
      this.closeAll()
    }
  }

  // While the sheet covers the page, Tab cycles inside the header.
  trapTab(event) {
    if (!this.sheetOpen || this.desktop) return
    const focusable = Array.from(this.element.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden"
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  escape() {
    if (this.sheetOpen) {
      this.setSheet(false)
      if (this.hasBurgerTarget) this.burgerTarget.focus()
      return
    }
    const open = this.itemTargets.find((item) => item.classList.contains("is-open"))
    if (!open) return
    this.closeAll()
    const trigger = open.querySelector("[data-site-nav-target~='trigger']")
    if (trigger) trigger.focus()
  }

  reset() {
    this.clearTimers()
    this.setSheet(false)
  }

  clearTimers() {
    clearTimeout(this.openTimer)
    clearTimeout(this.closeTimer)
  }
}
